import React, { useState, useMemo, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Checkbox,
  FormControlLabel,
  IconButton,
  Alert,
  CircularProgress,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { api } from "common";
import {
  getAuth,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import {
  getStorage,
  ref as stRef,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";
import {
  getDatabase,
  ref as dbRef,
  push,
  set,
  update,
  child,
} from "firebase/database";

// Brand tokens — match landing palette / typography exactly.
const BRAND = {
  teal: "#4fd1c5",
  tealDark: "#2dd4bf",
  slate950: "#020617",
  slate900: "#0f172a",
  slate700: "#334155",
  slate500: "#64748b",
  slate300: "#cbd5e1",
  slate200: "#e2e8f0",
  slate100: "#f1f5f9",
  slate50: "#f8fafc",
  font: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", sans-serif',
};

// ponytail: stesse parole dei parcelTypes nel DB (elenco unico dell'app); i valori sono le etichette stesse
const FACILITIES = [
  { value: "Carrozzina", label: "Carrozzina", icon: "♿" },
  { value: "Cintura contenitiva", label: "Cintura contenitiva", icon: "🔗" },
  { value: "Lettiga", label: "Lettiga", icon: "🛏️" },
  { value: "Ossigenoterapia", label: "Ossigenoterapia", icon: "💨" },
  { value: "Sedia basculante", label: "Sedia basculante", icon: "🪑" },
  { value: "Montascale", label: "Montascale", icon: "🪜" },
];

const initialState = {
  firstName: "",
  lastName: "",
  email: "",
  mobile: "",
  password: "",
  passwordConfirm: "",

  vehicleNumber: "",
  vehicleMake: "",
  vehicleModel: "",
  carType: "",
  other_info: "",
  availableFacilities: [],

  term: false,
  referralId: "",
};

const fileFieldsConfig = [
  { key: "licenseImage", label: "Patente — fronte", required: true },
  { key: "licenseImageBack", label: "Patente — retro", required: true },
  { key: "verifyIdImage", label: "Carta d'identità", required: false, hint: "opzionale" },
  { key: "profileImage", label: "Foto profilo", required: false, hint: "opzionale" },
  { key: "car_image", label: "Foto del veicolo", required: true },
  { key: "car_front_image", label: "Carta di circolazione — fronte", required: true },
  { key: "car_back_image", label: "Carta di circolazione — retro", required: true },
];

const SECTIONS = [
  { id: "personal", number: 1, title: "Dati personali", subtitle: "Le info essenziali del tuo account" },
  { id: "documents", number: 2, title: "Documenti", subtitle: "Patente di guida e identità" },
  { id: "vehicle", number: 3, title: "Veicolo", subtitle: "Targa, marca, modello e foto" },
  { id: "facilities", number: 4, title: "Ausili a disposizione", subtitle: "Cosa puoi trasportare" },
  { id: "confirm", number: 5, title: "Conferma", subtitle: "Termini e invio" },
];

export default function RegisterDriverPublic() {
  const navigate = useNavigate();
  const cartypes = useSelector((state) => state.cartypes.cars);

  const [form, setForm] = useState(initialState);
  const [files, setFiles] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [progressMsg, setProgressMsg] = useState("");
  const [success, setSuccess] = useState(false);
  const fileInputRefs = useRef({});

  // Inject Inter font from Google Fonts (matches landing). Once per mount.
  useEffect(() => {
    if (document.querySelector('link[data-medlift-inter]')) return;
    const link = document.createElement("link");
    link.href =
      "https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap";
    link.rel = "stylesheet";
    link.setAttribute("data-medlift-inter", "1");
    document.head.appendChild(link);
  }, []);

  const handleChange = (key) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((s) => ({ ...s, [key]: v }));
    if (errors[key]) setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const handleFacilityToggle = (value) => () => {
    setForm((s) => {
      const has = s.availableFacilities.includes(value);
      return {
        ...s,
        availableFacilities: has
          ? s.availableFacilities.filter((v) => v !== value)
          : [...s.availableFacilities, value],
      };
    });
  };

  const handleFile = (key) => (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      setErrors((er) => ({ ...er, [key]: "Il file deve essere un'immagine" }));
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      setErrors((er) => ({ ...er, [key]: "Massimo 10 MB" }));
      return;
    }
    setFiles((fs) => ({ ...fs, [key]: f }));
    if (errors[key]) setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const removeFile = (key) => () => {
    setFiles((fs) => {
      const copy = { ...fs };
      delete copy[key];
      return copy;
    });
    if (fileInputRefs.current[key]) fileInputRefs.current[key].value = "";
  };

  const validate = () => {
    const er = {};
    if (!form.firstName.trim()) er.firstName = "Obbligatorio";
    if (!form.lastName.trim()) er.lastName = "Obbligatorio";
    if (!form.email.trim()) er.email = "Obbligatorio";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      er.email = "Email non valida";
    if (!form.mobile.trim()) er.mobile = "Obbligatorio";
    else if (!/^\+\d{6,18}$/.test(form.mobile.replace(/\s/g, "")))
      er.mobile = "Formato internazionale richiesto, es. +41791234567";
    if (!form.password) er.password = "Obbligatorio";
    else if (form.password.length < 6) er.password = "Minimo 6 caratteri";
    if (form.password !== form.passwordConfirm)
      er.passwordConfirm = "Le password non coincidono";

    if (!form.vehicleNumber.trim()) er.vehicleNumber = "Obbligatorio";
    if (!form.vehicleMake.trim()) er.vehicleMake = "Obbligatorio";
    if (!form.vehicleModel.trim()) er.vehicleModel = "Obbligatorio";
    if (!form.carType) er.carType = "Seleziona un tipo veicolo";

    fileFieldsConfig.forEach((cfg) => {
      if (cfg.required && !files[cfg.key]) er[cfg.key] = "Foto obbligatoria";
    });

    if (!form.term) er.term = "Devi accettare i termini";

    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const uploadOne = async (storageRef, file) => {
    await uploadBytesResumable(storageRef, file);
    return await getDownloadURL(storageRef);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    if (!validate()) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setSubmitting(true);
    const auth = getAuth();
    const storage = getStorage();
    const database = getDatabase();
    let createdUid = null;

    try {
      setProgressMsg("Verifica email/telefono...");
      try {
        const exists = await api.checkUserExists({
          email: form.email,
          mobile: form.mobile.replace(/\s/g, ""),
        });
        if (exists && exists.users && exists.users.length > 0) {
          throw new Error(
            "Esiste già un account con questa email o numero di telefono."
          );
        }
      } catch (preErr) {
        if (
          preErr &&
          preErr.message &&
          preErr.message.startsWith("Esiste già")
        ) {
          throw preErr;
        }
        console.warn(
          "[register-driver] checkUserExists pre-check failed, proceeding:",
          preErr && preErr.message
        );
      }

      setProgressMsg("Creazione account...");
      const regData = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        mobile: form.mobile.replace(/\s/g, ""),
        password: form.password,
        usertype: "driver",
        approved: false,
        carApproved: false,
        driverActiveStatus: false,
        createdAt: Date.now(),
        term: !!form.term,
        referralId: form.referralId.trim() || "",
        signupViaReferral: "",
      };

      const signupRes = await api.mainSignUp(regData);
      if (!signupRes || !signupRes.uid) {
        const rawErr = (signupRes && signupRes.error) || "";
        throw new Error(
          rawErr === "User Not Created"
            ? "Impossibile creare l'account. Verifica che l'email non sia già registrata e che la password abbia almeno 6 caratteri."
            : rawErr || "Creazione account fallita. Riprova."
        );
      }
      createdUid = signupRes.uid;

      setProgressMsg("Accesso al sistema...");
      await signInWithEmailAndPassword(auth, form.email.trim(), form.password);

      setProgressMsg("Caricamento documenti...");
      const userImageUploads = {};
      const userImageMap = [
        { key: "licenseImage", path: `users/${createdUid}/license` },
        { key: "licenseImageBack", path: `users/${createdUid}/licenseBack` },
        { key: "verifyIdImage", path: `users/${createdUid}/verifyIdImage` },
        { key: "profileImage", path: `users/${createdUid}/profileImage` },
      ];
      for (const it of userImageMap) {
        if (files[it.key]) {
          userImageUploads[it.key] = await uploadOne(
            stRef(storage, it.path),
            files[it.key]
          );
        }
      }

      setProgressMsg("Caricamento foto veicolo...");
      const carAddRef = dbRef(database, "cars");
      const carId = push(carAddRef).key;

      const carImageMap = [
        { key: "car_image", path: `cars/${carId}` },
        { key: "car_front_image", path: `cars/${carId}front` },
        { key: "car_back_image", path: `cars/${carId}back` },
      ];
      const carImageUploads = {};
      for (const it of carImageMap) {
        if (files[it.key]) {
          carImageUploads[it.key] = await uploadOne(
            stRef(storage, it.path),
            files[it.key]
          );
        }
      }

      setProgressMsg("Salvataggio dati veicolo...");
      const carRecord = {
        active: true,
        approved: false,
        driver: createdUid,
        fleetadmin: null,
        carType: form.carType,
        vehicleNumber: form.vehicleNumber.trim(),
        vehicleMake: form.vehicleMake.trim(),
        vehicleModel: form.vehicleModel.trim(),
        other_info: form.other_info.trim(),
        availableFacilities: form.availableFacilities,
        selectedAid: form.availableFacilities,
        car_image: carImageUploads.car_image || "",
        car_front_image: carImageUploads.car_front_image || "",
        car_back_image: carImageUploads.car_back_image || "",
        createdAt: Date.now(),
      };
      await set(child(carAddRef, carId), carRecord);

      setProgressMsg("Completamento profilo...");
      const userPatch = {
        carType: form.carType,
        vehicleNumber: form.vehicleNumber.trim(),
        vehicleMake: form.vehicleMake.trim(),
        vehicleModel: form.vehicleModel.trim(),
        other_info: form.other_info.trim(),
        car_image: carImageUploads.car_image || "",
        car_front_image: carImageUploads.car_front_image || "",
        car_back_image: carImageUploads.car_back_image || "",
        carApproved: false,
        updateAt: Date.now(),
      };
      if (userImageUploads.licenseImage) {
        userPatch.licenseImage = userImageUploads.licenseImage;
      }
      if (userImageUploads.licenseImageBack) {
        userPatch.licenseImageBack = userImageUploads.licenseImageBack;
      }
      if (userImageUploads.verifyIdImage) {
        userPatch.verifyIdImage = userImageUploads.verifyIdImage;
      }
      if (userImageUploads.profileImage) {
        userPatch.profile_image = userImageUploads.profileImage;
      }
      await update(dbRef(database, `users/${createdUid}`), userPatch);

      setProgressMsg("Disconnessione...");
      try {
        await signOut(auth);
      } catch {}

      setSuccess(true);
      setSubmitting(false);
      setProgressMsg("");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error("[register-driver] error:", err);
      setSubmitError(
        err && err.message
          ? err.message
          : "Si è verificato un errore. Riprova."
      );
      setSubmitting(false);
      setProgressMsg("");
    }
  };

  // ───────────────────────────────────────── render helpers

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: "#ffffff",
      fontFamily: BRAND.font,
      fontSize: 15,
      "& fieldset": { borderColor: BRAND.slate200, borderWidth: "1.5px" },
      "&:hover fieldset": { borderColor: BRAND.slate300 },
      "&.Mui-focused fieldset": {
        borderColor: BRAND.teal,
        borderWidth: "2px",
      },
    },
    "& .MuiInputLabel-root": {
      fontFamily: BRAND.font,
      color: BRAND.slate500,
      "&.Mui-focused": { color: BRAND.teal },
    },
    "& .MuiFormHelperText-root": {
      fontFamily: BRAND.font,
      marginLeft: 0.5,
    },
  };

  const renderTextField = (key, label, opts = {}) => (
    <TextField
      fullWidth
      label={label}
      value={form[key]}
      onChange={handleChange(key)}
      error={!!errors[key]}
      helperText={errors[key] || opts.helper || ""}
      type={opts.type || "text"}
      multiline={!!opts.multiline}
      minRows={opts.multiline ? 3 : undefined}
      autoComplete={opts.autoComplete}
      inputProps={opts.inputProps}
      disabled={submitting}
      sx={inputSx}
    />
  );

  const renderFileField = (cfg) => {
    const file = files[cfg.key];
    const hasError = !!errors[cfg.key];
    return (
      <Box
        sx={{
          position: "relative",
          border: "2px dashed",
          borderColor: hasError ? "#ef4444" : file ? BRAND.teal : BRAND.slate200,
          borderRadius: "16px",
          p: 2,
          backgroundColor: file ? "rgba(79,209,197,0.04)" : "#fff",
          transition: "all 200ms cubic-bezier(0.4,0,0.2,1)",
          minHeight: 200,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          fontFamily: BRAND.font,
          "&:hover": {
            borderColor: hasError ? "#ef4444" : BRAND.teal,
            backgroundColor: file ? "rgba(79,209,197,0.06)" : BRAND.slate50,
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, minHeight: 36 }}>
          <Box
            sx={{
              fontWeight: 600,
              fontSize: 13,
              color: BRAND.slate900,
              lineHeight: 1.3,
              flex: 1,
              fontFamily: BRAND.font,
            }}
          >
            {cfg.label}
            {cfg.required && (
              <Box component="span" sx={{ color: "#ef4444", ml: 0.5 }}>
                *
              </Box>
            )}
            {cfg.hint && (
              <Box
                component="span"
                sx={{ color: BRAND.slate500, fontWeight: 400, fontSize: 11, ml: 0.5 }}
              >
                ({cfg.hint})
              </Box>
            )}
          </Box>
        </Box>
        <input
          ref={(el) => (fileInputRefs.current[cfg.key] = el)}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          id={`file-${cfg.key}`}
          onChange={handleFile(cfg.key)}
          disabled={submitting}
        />
        {!file ? (
          <Box
            component="label"
            htmlFor={`file-${cfg.key}`}
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              cursor: submitting ? "not-allowed" : "pointer",
              gap: 1,
              py: 2,
              opacity: submitting ? 0.5 : 1,
            }}
          >
            <CloudUploadIcon sx={{ color: BRAND.slate500, fontSize: 32 }} />
            <Typography
              sx={{
                fontFamily: BRAND.font,
                fontSize: 13,
                fontWeight: 600,
                color: BRAND.slate700,
              }}
            >
              Tocca per caricare
            </Typography>
            <Typography
              sx={{
                fontFamily: BRAND.font,
                fontSize: 11,
                color: BRAND.slate500,
              }}
            >
              JPG / PNG · max 10 MB
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box
              component="img"
              src={URL.createObjectURL(file)}
              alt="preview"
              sx={{
                width: "100%",
                height: 110,
                objectFit: "cover",
                borderRadius: "10px",
              }}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <CheckCircleIcon sx={{ color: BRAND.teal, fontSize: 16 }} />
              <Typography
                sx={{
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: 11,
                  color: BRAND.slate700,
                  fontFamily: BRAND.font,
                }}
                title={file.name}
              >
                {file.name}
              </Typography>
              <IconButton
                size="small"
                onClick={removeFile(cfg.key)}
                disabled={submitting}
                sx={{ p: 0.5, color: BRAND.slate500, "&:hover": { color: "#ef4444" } }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        )}
        {errors[cfg.key] && (
          <Typography
            sx={{ fontSize: 11, color: "#ef4444", fontFamily: BRAND.font, mt: 0.5 }}
          >
            {errors[cfg.key]}
          </Typography>
        )}
      </Box>
    );
  };

  const userFileKeys = useMemo(
    () =>
      fileFieldsConfig.filter(
        (f) =>
          f.key.startsWith("license") ||
          f.key === "verifyIdImage" ||
          f.key === "profileImage"
      ),
    []
  );
  const carFileKeys = useMemo(
    () => fileFieldsConfig.filter((f) => f.key.startsWith("car_")),
    []
  );

  // ───────────────────────────────────────── success screen

  if (success) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background: `linear-gradient(135deg, ${BRAND.slate950} 0%, ${BRAND.slate900} 50%, ${BRAND.slate950} 100%)`,
          fontFamily: BRAND.font,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: { xs: 2, md: 4 },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 600,
            height: 600,
            backgroundColor: "rgba(79,209,197,0.08)",
            borderRadius: "50%",
            filter: "blur(120px)",
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            maxWidth: 520,
            width: "100%",
            backgroundColor: "rgba(255,255,255,0.03)",
            backdropFilter: "blur(24px)",
            border: `1px solid rgba(255,255,255,0.08)`,
            borderRadius: "24px",
            p: { xs: 3, md: 6 },
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              backgroundColor: "rgba(79,209,197,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 24px",
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 40, color: BRAND.teal }} />
          </Box>
          <Typography
            sx={{
              color: "#fff",
              fontWeight: 700,
              fontSize: { xs: 24, md: 32 },
              fontFamily: BRAND.font,
              mb: 2,
              letterSpacing: "-0.02em",
            }}
          >
            Registrazione completata
          </Typography>
          <Typography
            sx={{
              color: BRAND.slate300,
              fontSize: 15,
              lineHeight: 1.6,
              fontFamily: BRAND.font,
              mb: 1,
            }}
          >
            Il tuo account è stato creato. La struttura controllerà i documenti
            che hai caricato e ti abiliterà al servizio.
          </Typography>
          <Typography
            sx={{
              color: BRAND.slate500,
              fontSize: 13,
              fontFamily: BRAND.font,
              mb: 4,
            }}
          >
            Riceverai conferma quando sarai pronto. Nel frattempo puoi scaricare
            l'app MedLift e fare il login con email e password.
          </Typography>
          <Button
            onClick={() => navigate("/login")}
            disableElevation
            sx={{
              backgroundColor: BRAND.teal,
              color: BRAND.slate900,
              fontFamily: BRAND.font,
              fontWeight: 700,
              fontSize: 15,
              borderRadius: "12px",
              py: 1.5,
              px: 4,
              textTransform: "none",
              boxShadow: "0 8px 24px -8px rgba(79,209,197,0.5)",
              "&:hover": {
                backgroundColor: BRAND.tealDark,
                boxShadow: "0 12px 32px -8px rgba(79,209,197,0.6)",
              },
            }}
          >
            Vai al login →
          </Button>
        </Box>
      </Box>
    );
  }

  // ───────────────────────────────────────── form screen

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: BRAND.slate50,
        fontFamily: BRAND.font,
        position: "relative",
      }}
    >
      <style>{KEYFRAMES_CSS}</style>

      {/* Hero header — dark slate, matches landing #problemi/#trasportatore sections */}
      <Box
        sx={{
          position: "relative",
          background: `linear-gradient(180deg, ${BRAND.slate950} 0%, ${BRAND.slate900} 100%)`,
          color: "#fff",
          overflow: "hidden",
          pt: { xs: 3, md: 5 },
          pb: { xs: 5, md: 8 },
          px: { xs: 2, md: 4 },
        }}
      >
        {/* Ambient teal glow */}
        <Box
          sx={{
            position: "absolute",
            top: "-20%",
            right: "-10%",
            width: 500,
            height: 500,
            backgroundColor: "rgba(79,209,197,0.08)",
            borderRadius: "50%",
            filter: "blur(120px)",
            pointerEvents: "none",
          }}
        />
        {/* Subtle grid pattern */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "linear-gradient(rgba(79,209,197,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(79,209,197,0.04) 1px, transparent 1px)",
            backgroundSize: "40px 40px",
            pointerEvents: "none",
          }}
        />

        <Box sx={{ maxWidth: 720, mx: "auto", position: "relative", zIndex: 2 }}>
          {/* Top bar: back link + logo */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: { xs: 4, md: 6 },
            }}
          >
            <Box
              component="a"
              href="/"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                color: BRAND.slate300,
                textDecoration: "none",
                fontSize: 13,
                fontWeight: 500,
                fontFamily: BRAND.font,
                px: 1.5,
                py: 1,
                borderRadius: "10px",
                transition: "all 200ms",
                "&:hover": { color: "#fff", backgroundColor: "rgba(255,255,255,0.05)" },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 16 }} /> Torna al sito
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Box
                component="img"
                src="/landing/Images/Frame 45.svg"
                alt="MedLift"
                sx={{ width: 32, height: 32, borderRadius: "8px" }}
              />
              <Typography
                sx={{
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 16,
                  letterSpacing: "-0.025em",
                  fontFamily: BRAND.font,
                }}
              >
                MedLift
              </Typography>
            </Box>
          </Box>

          {/* Title */}
          <Box sx={{ animation: "ml-fade-up 600ms cubic-bezier(0.16,1,0.3,1) both" }}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                px: 1.5,
                py: 0.5,
                borderRadius: "999px",
                backgroundColor: "rgba(79,209,197,0.15)",
                border: `1px solid rgba(79,209,197,0.3)`,
                mb: 2,
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  backgroundColor: BRAND.teal,
                  animation: "ml-pulse 2s ease-in-out infinite",
                }}
              />
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: BRAND.teal,
                  fontFamily: BRAND.font,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                }}
              >
                Iscrizioni aperte
              </Typography>
            </Box>
            <Typography
              sx={{
                fontFamily: BRAND.font,
                fontWeight: 700,
                fontSize: { xs: 28, sm: 36, md: 44 },
                lineHeight: 1.1,
                letterSpacing: "-0.025em",
                color: "#fff",
                mb: 1.5,
              }}
            >
              Diventa Trasportatore
              <Box component="span" sx={{ color: BRAND.teal }}> MedLift</Box>
            </Typography>
            <Typography
              sx={{
                fontFamily: BRAND.font,
                fontSize: { xs: 14, md: 16 },
                color: BRAND.slate300,
                lineHeight: 1.6,
                maxWidth: 540,
              }}
            >
              Compila il modulo con i tuoi dati, documenti e foto del veicolo.
              Una struttura sanitaria controllerà i tuoi documenti prima di
              abilitarti al servizio.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Form area */}
      <Box
        sx={{
          maxWidth: 720,
          mx: "auto",
          px: { xs: 2, md: 4 },
          mt: { xs: -3, md: -4 },
          pb: { xs: 12, md: 8 }, // bottom padding for sticky submit on mobile
          position: "relative",
          zIndex: 3,
        }}
      >
        {submitError && (
          <Alert
            severity="error"
            onClose={() => setSubmitError(null)}
            sx={{
              mb: 3,
              borderRadius: "14px",
              fontFamily: BRAND.font,
              border: "1px solid #fecaca",
            }}
          >
            {submitError}
          </Alert>
        )}

        <form onSubmit={handleSubmit} noValidate>
          {/* SECTION 1 — Anagrafica */}
          <SectionCard section={SECTIONS[0]}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              {renderTextField("firstName", "Nome", { autoComplete: "given-name" })}
              {renderTextField("lastName", "Cognome", { autoComplete: "family-name" })}
              {renderTextField("email", "Email", { type: "email", autoComplete: "email" })}
              {renderTextField("mobile", "Telefono", {
                helper: "Formato internazionale, es. +41791234567",
                autoComplete: "tel",
              })}
              {renderTextField("password", "Password", {
                type: "password",
                autoComplete: "new-password",
                helper: "Minimo 6 caratteri",
              })}
              {renderTextField("passwordConfirm", "Ripeti password", {
                type: "password",
                autoComplete: "new-password",
              })}
            </Box>
          </SectionCard>

          {/* SECTION 2 — Documenti */}
          <SectionCard section={SECTIONS[1]}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              {userFileKeys.map((cfg) => (
                <React.Fragment key={cfg.key}>{renderFileField(cfg)}</React.Fragment>
              ))}
            </Box>
          </SectionCard>

          {/* SECTION 3 — Veicolo */}
          <SectionCard section={SECTIONS[2]}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
                mb: 2.5,
              }}
            >
              <TextField
                select
                fullWidth
                label="Tipo veicolo"
                value={form.carType}
                onChange={handleChange("carType")}
                error={!!errors.carType}
                helperText={errors.carType || ""}
                disabled={submitting}
                sx={inputSx}
              >
                {(cartypes || []).map((ct) => (
                  <MenuItem
                    key={ct.id}
                    value={ct.name || ct.id}
                    sx={{ fontFamily: BRAND.font }}
                  >
                    {ct.name || ct.id}
                  </MenuItem>
                ))}
                {(!cartypes || cartypes.length === 0) && (
                  <MenuItem value="" disabled sx={{ fontFamily: BRAND.font }}>
                    Caricamento tipi veicolo...
                  </MenuItem>
                )}
              </TextField>
              {renderTextField("vehicleNumber", "Targa")}
              {renderTextField("vehicleMake", "Marca")}
              {renderTextField("vehicleModel", "Modello")}
              <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
                {renderTextField("other_info", "Note (opzionale)", { multiline: true })}
              </Box>
            </Box>

            <SectionLabel>Foto veicolo</SectionLabel>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                gap: 2,
              }}
            >
              {carFileKeys.map((cfg) => (
                <React.Fragment key={cfg.key}>{renderFileField(cfg)}</React.Fragment>
              ))}
            </Box>
          </SectionCard>

          {/* SECTION 4 — Ausili */}
          <SectionCard section={SECTIONS[3]}>
            <Typography
              sx={{
                fontSize: 13,
                color: BRAND.slate500,
                mb: 2,
                fontFamily: BRAND.font,
              }}
            >
              Seleziona gli ausili che il tuo veicolo può fornire (opzionale).
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr" },
                gap: 1.25,
              }}
            >
              {FACILITIES.map((f) => {
                const checked = form.availableFacilities.includes(f.value);
                return (
                  <Box
                    key={f.value}
                    component="button"
                    type="button"
                    onClick={handleFacilityToggle(f.value)}
                    disabled={submitting}
                    sx={{
                      cursor: submitting ? "not-allowed" : "pointer",
                      backgroundColor: checked ? "rgba(79,209,197,0.08)" : "#fff",
                      border: `2px solid ${checked ? BRAND.teal : BRAND.slate200}`,
                      borderRadius: "12px",
                      p: 1.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      fontFamily: BRAND.font,
                      fontSize: 14,
                      fontWeight: 500,
                      color: checked ? BRAND.slate900 : BRAND.slate700,
                      textAlign: "left",
                      transition: "all 200ms",
                      "&:hover": !submitting && {
                        borderColor: BRAND.teal,
                        backgroundColor: "rgba(79,209,197,0.04)",
                      },
                    }}
                  >
                    <Box sx={{ fontSize: 20 }}>{f.icon}</Box>
                    <Box sx={{ flex: 1 }}>{f.label}</Box>
                    {checked && (
                      <CheckCircleIcon sx={{ color: BRAND.teal, fontSize: 18 }} />
                    )}
                  </Box>
                );
              })}
            </Box>
          </SectionCard>

          {/* SECTION 5 — Conferma */}
          <SectionCard section={SECTIONS[4]}>
            {renderTextField("referralId", "Codice referral (opzionale)")}
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.term}
                    onChange={handleChange("term")}
                    disabled={submitting}
                    sx={{
                      color: BRAND.slate300,
                      "&.Mui-checked": { color: BRAND.teal },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: BRAND.slate700,
                      lineHeight: 1.5,
                      fontFamily: BRAND.font,
                    }}
                  >
                    Accetto i{" "}
                    <Box
                      component="a"
                      href="/term-condition"
                      target="_blank"
                      rel="noreferrer"
                      sx={{
                        color: BRAND.teal,
                        textDecoration: "none",
                        fontWeight: 600,
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      Termini e Condizioni
                    </Box>{" "}
                    e la{" "}
                    <Box
                      component="a"
                      href="/privacy-policy"
                      target="_blank"
                      rel="noreferrer"
                      sx={{
                        color: BRAND.teal,
                        textDecoration: "none",
                        fontWeight: 600,
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      Privacy Policy
                    </Box>
                    .
                  </Typography>
                }
              />
              {errors.term && (
                <Typography
                  sx={{
                    fontSize: 11,
                    color: "#ef4444",
                    fontFamily: BRAND.font,
                    mt: 0.5,
                    ml: 4,
                  }}
                >
                  {errors.term}
                </Typography>
              )}
            </Box>
          </SectionCard>

          {/* Submit — desktop inline, sticky on mobile */}
          <Box
            sx={{
              position: { xs: "fixed", md: "static" },
              bottom: { xs: 0, md: "auto" },
              left: { xs: 0, md: "auto" },
              right: { xs: 0, md: "auto" },
              backgroundColor: { xs: "rgba(248,250,252,0.92)", md: "transparent" },
              backdropFilter: { xs: "blur(12px)", md: "none" },
              borderTop: { xs: `1px solid ${BRAND.slate200}`, md: "none" },
              p: { xs: 2, md: 0 },
              mt: { xs: 0, md: 4 },
              zIndex: 10,
            }}
          >
            <Box sx={{ maxWidth: 720, mx: "auto", display: "flex", justifyContent: "center" }}>
              <Button
                type="submit"
                disabled={submitting}
                disableElevation
                fullWidth
                startIcon={
                  submitting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : null
                }
                endIcon={!submitting ? <ArrowForwardIcon /> : null}
                sx={{
                  maxWidth: { xs: "100%", md: 360 },
                  py: 1.75,
                  borderRadius: "14px",
                  backgroundColor: BRAND.slate900,
                  color: "#fff",
                  fontFamily: BRAND.font,
                  fontWeight: 700,
                  fontSize: 15,
                  textTransform: "none",
                  boxShadow: `0 12px 32px -8px ${BRAND.slate900}40`,
                  transition: "all 200ms",
                  "&:hover": {
                    backgroundColor: BRAND.slate950,
                    transform: "translateY(-1px)",
                    boxShadow: `0 16px 40px -8px ${BRAND.slate900}60`,
                  },
                  "&.Mui-disabled": {
                    backgroundColor: BRAND.slate900,
                    color: "rgba(255,255,255,0.7)",
                  },
                }}
              >
                {submitting ? progressMsg || "Invio in corso..." : "Invia richiesta"}
              </Button>
            </Box>
          </Box>
        </form>
      </Box>
    </Box>
  );
}

// ───────────────────────────────────────── inline subcomponents

function SectionCard({ section, children }) {
  return (
    <Box
      component="section"
      id={section.id}
      sx={{
        backgroundColor: "#fff",
        border: `1px solid ${BRAND.slate200}`,
        borderRadius: "20px",
        p: { xs: 2.5, md: 4 },
        mb: 3,
        boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 4px 16px -4px rgba(15,23,42,0.04)",
        animation: "ml-fade-up 600ms cubic-bezier(0.16,1,0.3,1) both",
        animationDelay: `${section.number * 80}ms`,
        opacity: 0,
        fontFamily: BRAND.font,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2.5 }}>
        <Box
          sx={{
            flexShrink: 0,
            width: 36,
            height: 36,
            borderRadius: "10px",
            backgroundColor: "rgba(79,209,197,0.12)",
            border: `1.5px solid rgba(79,209,197,0.35)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: BRAND.teal,
            fontWeight: 700,
            fontSize: 15,
            fontFamily: BRAND.font,
          }}
        >
          {section.number}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: { xs: 17, md: 19 },
              fontWeight: 700,
              color: BRAND.slate900,
              lineHeight: 1.2,
              letterSpacing: "-0.015em",
              fontFamily: BRAND.font,
            }}
          >
            {section.title}
          </Typography>
          {section.subtitle && (
            <Typography
              sx={{
                fontSize: 13,
                color: BRAND.slate500,
                mt: 0.25,
                fontFamily: BRAND.font,
              }}
            >
              {section.subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      {children}
    </Box>
  );
}

function SectionLabel({ children }) {
  return (
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 700,
        color: BRAND.slate500,
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        mb: 1.5,
        mt: 1,
        fontFamily: BRAND.font,
      }}
    >
      {children}
    </Typography>
  );
}

const KEYFRAMES_CSS = `
@keyframes ml-fade-up {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes ml-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}
@media (prefers-reduced-motion: reduce) {
  [style*="ml-fade-up"], [style*="ml-pulse"] { animation: none !important; opacity: 1 !important; }
}
`;
